/**
 * The Medium tier's frame: the scene into a linear HDR target, its depth
 * packed into a half-resolution snapshot, then one full-screen lens pass
 * (`shaders/lens.ts`) to the canvas with the output tone mapping (lifted from
 * the shared prototype engine's `engine/src/focus.js` `DepthSnapshotPass` and
 * the dungeon-floors prototype's composer in `dungeon-main.js`).
 *
 * Hand-rolled rather than three's `EffectComposer`: the composer needs a
 * `RenderPass`, a depth-snapshot pass subclass, a `ShaderPass`, an
 * `OutputPass` and its own ping-pong targets with depth textures on both;
 * here there are two targets and two full-screen triangles, the output tone
 * mapping is the lens pass's last line (one full-screen pass fewer than the
 * prototype). The snapshot is a copy because the coronas and shafts read
 * depth while the next frame's beauty target is bound (a texture cannot be
 * sampled while it is attached).
 *
 * Slice 6a adds what lantern-water's stack has, as options the dungeon leaves
 * off (`LensOptions`): the town's slot bodies (`townSlots`), an `overlay`
 * drawn into the beauty target after the depth snapshot (the traveler's
 * selective silhouette, `visibility.js`), contact occlusion (three's own
 * `GTAOShader` from `three/addons`, as the engine's `occlusion.js` used it: a
 * half-resolution AO and a bilateral denoise, composited inside the lens
 * pass) and bloom (three's own `UnrealBloomPass` from `three/addons`, run on
 * the half-float beauty target and blended into it before the lens pass;
 * only where half-float targets render). The EffectComposer is still not
 * used: these are passes this module runs in order, and the lens is one pass.
 * The order is the prototype's: the scene, the depth snapshot, the overlay,
 * the occlusion composited into its own target (`aoCompositeFragment`), bloom
 * on that target, then the lens reading it, so every tap the focus gathers
 * and everything bloom extracts is already occluded.
 *
 * `release` (tier Off) frees the targets, the triangle and the full-screen
 * materials' programs (the lens, the snapshot, GTAO, the denoise and the
 * composite; the dungeon's rule: Off disposes Medium materials); the next
 * Medium frame compiles them again.
 */
import * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { GTAOShader, generateMagicSquareNoise } from 'three/addons/shaders/GTAOShader.js';
import { presentation } from './constants.ts';
import { type FocusSlots, aoCompositeFragment, aoDenoiseFragment, aoProbeFragment, dungeonSlots, focusFragment, fullscreenVertex, snapshotFragment } from './shaders/lens.ts';

export type LensUniforms = ReturnType<typeof lensUniforms>;

const lensUniforms = () => ({
  tDiffuse: { value: null as THREE.Texture | null },
  tDepth: { value: null as THREE.Texture | null },
  zoneMap: { value: null as THREE.Texture | null },
  resolution: { value: new THREE.Vector2(1, 1) },
  time: { value: 0 },
  focusDistance: { value: 10 },
  cameraNear: { value: presentation.camera.near as number },
  cameraFar: { value: presentation.camera.far as number },
  strength: { value: presentation.medium.lens.strength as number },
  night: { value: 0 },
  gradeShadow: { value: new THREE.Vector3(1, 1, 1) },
  gradeHighlight: { value: new THREE.Vector3(1, 1, 1) },
  gradeLift: { value: new THREE.Vector3() },
  gradeGain: { value: new THREE.Vector3(1, 1, 1) },
  gradeSaturation: { value: 1 },
  gradeContrast: { value: 1 },
  gradeVignette: { value: new THREE.Vector3(1, 1, 1) },
  hazeColor: { value: new THREE.Vector3() },
  hazeAmount: { value: 0 },
  hazeRange: { value: new THREE.Vector2(...presentation.medium.lens.hazeRange) },
  zoneRect: { value: new THREE.Vector4(0, 0, 1, 1) },
  zoneStrength: { value: 0 },
  zoneGround: { value: presentation.medium.lens.zoneGround as number },
  projScale: { value: new THREE.Vector2(1, 1) },
  viewToWorld: { value: new THREE.Matrix4() },
  shadowLift: { value: new THREE.Vector3() },
  shadowChroma: { value: presentation.medium.lens.shadowChroma as number },
  focusBand: { value: new THREE.Vector2(0.5, 1) },
  tiltRamp: { value: new THREE.Vector4(...presentation.medium.lens.tiltRampTop, 0, 1) },
  tiltScale: { value: new THREE.Vector2(1, 1) },
  blurPx: { value: 1 },
  grain: { value: 0 },
  dust: { value: 0 },
  edgeHaze: { value: new THREE.Vector4() },
  edgeHazeTop: { value: new THREE.Vector3() },
  edgeHazeBottom: { value: new THREE.Vector3() },
  zoneMood: { value: new THREE.Vector3() },
  waterMask: { value: 0 },
  waterBand: { value: new THREE.Vector2(-0.66, -0.55) },
  glare: { value: 0 },
  sunUv: { value: new THREE.Vector2(-0.2, 1.15) },
});

/** What a lens adds to the dungeon's (slice 6a): another slot set, contact occlusion, bloom. */
export type LensOptions = {
  readonly slots?: FocusSlots;
  /** Contact occlusion's GTAO: samples, world radius, thickness, distance exponent, strength in the composite. */
  readonly occlusion?: { readonly samples: number; readonly radius: number; readonly thickness: number; readonly distanceExponent: number; readonly intensity: number } | null;
  /** Bloom's radius and threshold (the strength is set per frame); only on a half-float target. `scale` sizes its targets against the frame (the airship's half size: `airship-main.js` `resize`). */
  readonly bloom?: { readonly radius: number; readonly threshold: number; readonly scale?: number } | null;
  /** Uniforms the slot bodies declare beyond the lens's own (the airship's far layer, ADR 0033), shared by reference with the caller. */
  readonly uniforms?: Readonly<Record<string, THREE.IUniform>>;
};

/** Drawn into the beauty target after the depth snapshot and before occlusion, bloom and the lens. */
export type LensOverlay = (renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget) => void;

export type Lens = {
  readonly uniforms: LensUniforms;
  /** The slot bodies' own uniforms (`LensOptions.uniforms`). */
  readonly extra: Readonly<Record<string, THREE.IUniform>>;
  /** The half-resolution depth snapshot (borrowed by coronas and shafts). */
  readonly depth: THREE.Texture;
  /** The beauty target: the scene renders into it (also the target programs are precompiled for). */
  readonly target: THREE.WebGLRenderTarget;
  /** The full-screen materials, for precompiling. */
  readonly materials: readonly THREE.Material[];
  setSize(width: number, height: number): void;
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera, overlay?: LensOverlay): void;
  /** Bloom's strength this frame (0 skips it). */
  setBloom(strength: number): void;
  /** Contact occlusion on or off (on only when the lens was made with it). */
  setOcclusion(on: boolean): void;
  /** Frees the targets', the triangle's and the full-screen materials' GPU copies and programs (tier Off); the next render makes them again. */
  release(): void;
  /**
   * Tests: forces the occlusion to a constant (`null`: computed again), skipping GTAO; and reads, after the last
   * render, the composite over the beauty image at a point (uv) and the beauty's brightest channel there (null
   * without occlusion). The lens reads the composite whenever occlusion is on (`lensInput`).
   */
  forceOcclusion(ao: number | null): void;
  probeOcclusion(renderer: THREE.WebGLRenderer, u: number, v: number): { readonly ratio: number; readonly beauty: number; readonly lensInput: 'composite' | 'beauty' } | null;
  dispose(): void;
};

export const createLens = (halfFloat: boolean, options: LensOptions = {}): Lens => {
  const depthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType);
  depthTexture.minFilter = THREE.NearestFilter;
  depthTexture.magFilter = THREE.NearestFilter;
  const target = new THREE.WebGLRenderTarget(1, 1, { type: halfFloat ? THREE.HalfFloatType : THREE.UnsignedByteType, depthTexture, depthBuffer: true, stencilBuffer: false });
  const snapshot = new THREE.WebGLRenderTarget(1, 1, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false });
  const snapshotMaterial = new THREE.ShaderMaterial({
    uniforms: { sourceDepth: { value: depthTexture } },
    vertexShader: fullscreenVertex,
    fragmentShader: snapshotFragment,
    // It renders into its own target, never to the screen: no output tone mapping, so the precompiled program is the one used.
    toneMapped: false,
    depthTest: false,
    depthWrite: false,
    blending: THREE.NoBlending,
  });
  const uniforms = lensUniforms();
  uniforms.tDiffuse.value = target.texture;
  uniforms.tDepth.value = snapshot.texture;
  const extra = options.uniforms ?? {};
  const lensMaterial = new THREE.ShaderMaterial({
    uniforms: { ...uniforms, ...extra },
    vertexShader: fullscreenVertex,
    fragmentShader: focusFragment(options.slots ?? dungeonSlots(presentation.medium.lens.taps)),
    depthTest: false,
    depthWrite: false,
    blending: THREE.NoBlending,
  });
  // One triangle covering the screen, no camera: the vertex shader writes clip space directly.
  const triangle = new THREE.BufferGeometry();
  triangle.setAttribute('position', new THREE.Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
  triangle.setAttribute('uv', new THREE.Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));
  const quad = new THREE.Mesh<THREE.BufferGeometry, THREE.Material>(triangle, lensMaterial);
  quad.frustumCulled = false;
  const post = new THREE.Scene();
  post.add(quad);
  const postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const size = new THREE.Vector2(1, 1);
  const pass = (renderer: THREE.WebGLRenderer, material: THREE.Material, into: THREE.WebGLRenderTarget | null): void => {
    quad.material = material;
    renderer.setRenderTarget(into);
    renderer.render(post, postCamera);
  };
  // Bloom (three's UnrealBloomPass, run directly): only on a half-float target, as the prototype.
  const bloom = options.bloom && halfFloat ? new UnrealBloomPass(new THREE.Vector2(1, 1), 0, options.bloom.radius, options.bloom.threshold) : null;
  let bloomStrength = 0;
  // Contact occlusion (the engine's ContactOcclusionPass, composited in the lens pass): raw AO and its denoise at half size.
  const ao = (() => {
    const o = options.occlusion;
    if (o === undefined || o === null) return null;
    const raw = new THREE.WebGLRenderTarget(1, 1, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false });
    const filtered = raw.clone();
    const noise = generateMagicSquareNoise();
    const gtao = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(GTAOShader.uniforms),
      defines: { ...GTAOShader.defines, NORMAL_VECTOR_TYPE: 0, SAMPLES: o.samples },
      vertexShader: GTAOShader.vertexShader,
      fragmentShader: GTAOShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: false,
    });
    Object.assign(gtao.uniforms, { tNoise: { value: noise }, radius: { value: o.radius }, thickness: { value: o.thickness }, distanceExponent: { value: o.distanceExponent } });
    const denoise = new THREE.ShaderMaterial({
      uniforms: { tDepth: { value: depthTexture }, tAO: { value: raw.texture }, resolution: { value: new THREE.Vector2(1, 1) }, cameraNear: { value: 1 }, cameraFar: { value: 100 } },
      vertexShader: fullscreenVertex,
      fragmentShader: aoDenoiseFragment,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: false,
    });
    // The occluded image: full resolution, the beauty target's type (bloom and the lens read it).
    const occluded = new THREE.WebGLRenderTarget(1, 1, { type: halfFloat ? THREE.HalfFloatType : THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false });
    const composite = new THREE.ShaderMaterial({
      uniforms: { tDepth: { value: depthTexture }, tAO: { value: filtered.texture }, tDiffuse: { value: target.texture }, resolution: { value: new THREE.Vector2(1, 1) }, cameraNear: { value: 1 }, cameraFar: { value: 100 }, intensity: { value: o.intensity } },
      vertexShader: fullscreenVertex,
      fragmentShader: aoCompositeFragment,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: false,
    });
    return { raw, filtered, noise, gtao, denoise, occluded, composite };
  })();
  let forced: number | null = null;
  const forcedColor = new THREE.Color();
  // The test probe: one pixel, 8 bits, the composite's ratio to the beauty image.
  const probeTarget = new THREE.WebGLRenderTarget(1, 1, { depthBuffer: false, stencilBuffer: false });
  const probeMaterial = new THREE.ShaderMaterial({
    uniforms: { tBeauty: { value: target.texture }, tComposite: { value: null as THREE.Texture | null }, at: { value: new THREE.Vector2() } },
    vertexShader: fullscreenVertex,
    fragmentShader: aoProbeFragment,
    depthTest: false,
    depthWrite: false,
    blending: THREE.NoBlending,
    toneMapped: false,
  });
  const probePixel = new Uint8Array(4);
  let aoOn = ao !== null;
  const white = new THREE.Color(1, 1, 1);
  const clearColor = new THREE.Color();
  return {
    uniforms,
    extra,
    depth: snapshot.texture,
    target,
    materials: [lensMaterial, snapshotMaterial, ...(ao === null ? [] : [ao.gtao, ao.denoise, ao.composite])],
    setSize(width, height) {
      size.set(Math.max(1, width), Math.max(1, height));
      target.setSize(size.x, size.y);
      snapshot.setSize(Math.max(1, Math.ceil(size.x / 2)), Math.max(1, Math.ceil(size.y / 2)));
      uniforms.resolution.value.copy(size);
      uniforms.blurPx.value = Math.min(size.x, size.y) * presentation.medium.lens.blurShortEdge;
      const bloomScale = options.bloom?.scale ?? 1;
      bloom?.setSize(Math.max(1, size.x * bloomScale), Math.max(1, size.y * bloomScale));
      if (ao !== null) {
        const w = Math.max(1, Math.ceil(size.x / 2));
        const h = Math.max(1, Math.ceil(size.y / 2));
        ao.raw.setSize(w, h);
        ao.filtered.setSize(w, h);
        ao.occluded.setSize(size.x, size.y);
        for (const m of [ao.gtao, ao.denoise, ao.composite]) (m.uniforms['resolution']?.value as THREE.Vector2 | undefined)?.set(w, h);
      }
    },
    render(renderer, scene, camera, overlay) {
      uniforms.projScale.value.set(camera.projectionMatrix.elements[0] ?? 1, camera.projectionMatrix.elements[5] ?? 1);
      uniforms.viewToWorld.value.copy(camera.matrixWorld);
      uniforms.cameraNear.value = camera.near;
      uniforms.cameraFar.value = camera.far;
      renderer.setRenderTarget(target);
      renderer.render(scene, camera);
      pass(renderer, snapshotMaterial, snapshot);
      overlay?.(renderer, target);
      let image = target;
      if (ao !== null && aoOn) {
        const u = ao.gtao.uniforms;
        const set = (name: string, value: unknown): void => {
          const slot = u[name];
          if (slot !== undefined) slot.value = value;
        };
        set('tDepth', depthTexture);
        set('cameraNear', camera.near);
        set('cameraFar', camera.far);
        (u['cameraProjectionMatrix']?.value as THREE.Matrix4 | undefined)?.copy(camera.projectionMatrix);
        (u['cameraProjectionMatrixInverse']?.value as THREE.Matrix4 | undefined)?.copy(camera.projectionMatrixInverse);
        (u['cameraWorldMatrix']?.value as THREE.Matrix4 | undefined)?.copy(camera.matrixWorld);
        for (const m of [ao.denoise, ao.composite]) {
          const d = m.uniforms;
          if (d['cameraNear'] !== undefined) d['cameraNear'].value = camera.near;
          if (d['cameraFar'] !== undefined) d['cameraFar'].value = camera.far;
        }
        renderer.getClearColor(clearColor);
        const alpha = renderer.getClearAlpha();
        if (forced === null) {
          renderer.setClearColor(white, 1);
          renderer.setRenderTarget(ao.raw);
          renderer.clear();
          pass(renderer, ao.gtao, ao.raw);
          pass(renderer, ao.denoise, ao.filtered);
        } else {
          renderer.setClearColor(forcedColor.setRGB(forced, forced, forced, THREE.LinearSRGBColorSpace), 1);
          renderer.setRenderTarget(ao.filtered);
          renderer.clear();
        }
        renderer.setClearColor(clearColor, alpha);
        pass(renderer, ao.composite, ao.occluded);
        image = ao.occluded;
      }
      uniforms.tDiffuse.value = image.texture;
      if (bloom !== null && bloomStrength > 0) {
        bloom.strength = bloomStrength;
        bloom.render(renderer, image, image, 0, false);
      }
      pass(renderer, lensMaterial, null);
    },
    forceOcclusion(value) {
      forced = value;
    },
    probeOcclusion(renderer, u, v) {
      if (ao === null || !aoOn) return null;
      (probeMaterial.uniforms['tComposite'] as THREE.IUniform<THREE.Texture | null>).value = ao.occluded.texture;
      (probeMaterial.uniforms['at']?.value as THREE.Vector2).set(u, v);
      pass(renderer, probeMaterial, probeTarget);
      renderer.readRenderTargetPixels(probeTarget, 0, 0, 1, 1, probePixel);
      renderer.setRenderTarget(null);
      return { ratio: ((probePixel[0] ?? 0) / 255) * 2, beauty: ((probePixel[1] ?? 0) / 255) * 2, lensInput: uniforms.tDiffuse.value === ao.occluded.texture ? 'composite' : 'beauty' };
    },
    setBloom(strength) {
      bloomStrength = strength;
    },
    setOcclusion(on) {
      aoOn = on && ao !== null;
    },
    release() {
      target.dispose();
      snapshot.dispose();
      triangle.dispose();
      snapshotMaterial.dispose();
      lensMaterial.dispose();
      if (ao !== null) for (const x of [ao.raw, ao.filtered, ao.noise, ao.occluded, ao.gtao, ao.denoise, ao.composite]) x.dispose();
      probeTarget.dispose();
      probeMaterial.dispose();
      bloom?.dispose();
    },
    dispose() {
      target.dispose();
      snapshot.dispose();
      snapshotMaterial.dispose();
      lensMaterial.dispose();
      triangle.dispose();
      if (ao !== null) for (const x of [ao.raw, ao.filtered, ao.noise, ao.occluded, ao.gtao, ao.denoise, ao.composite]) x.dispose();
      probeTarget.dispose();
      probeMaterial.dispose();
      bloom?.dispose();
    },
  };
};
